import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { GripVertical, Pencil, Trash2 } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { AdminListPage } from '../components/AdminListPage'
import { useAdminProjects } from '../hooks/useAdminProjects'
import { adminFilmsService } from '../services/admin.films.service'
import type { FilmDocument } from '@/types'

type Project = FilmDocument & { id: string }

interface SortableProjectRowProps {
  project: Project
  deletingId: string | null
  onEdit: (id: string) => void
  onDelete: (project: Project) => void
}

function SortableProjectRow({ project, deletingId, onEdit, onDelete }: SortableProjectRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: project.id,
  })

  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <TableCell className="w-8 pr-0">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell className="font-medium">{project.title}</TableCell>
      <TableCell>
        <Badge variant={project.isPublished ? 'default' : 'secondary'}>
          {project.isPublished ? 'Published' : 'Draft'}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end">
          <MultiButton
            variant="outline"
            size="sm"
            items={[
              { id: 'edit', icon: Pencil, label: 'Edit', onClick: () => onEdit(project.id) },
              {
                id: 'delete',
                icon: Trash2,
                label: 'Delete',
                onClick: () => onDelete(project),
                disabled: deletingId === project.id,
                className: 'text-destructive hover:text-destructive',
              },
            ]}
          />
        </div>
      </TableCell>
    </TableRow>
  )
}

export function AdminProjectsPage() {
  const navigate = useNavigate()
  const { projects, loading, error, deleteProject, refresh } = useAdminProjects()
  const [orderedProjects, setOrderedProjects] = useState<Project[]>([])
  const [savingOrder, setSavingOrder] = useState(false)
  const [orderError, setOrderError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    setOrderedProjects([...projects].sort((a, b) => a.order - b.order))
  }, [projects])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = orderedProjects.findIndex((p) => p.id === active.id)
    const newIndex = orderedProjects.findIndex((p) => p.id === over.id)
    const reordered = arrayMove(orderedProjects, oldIndex, newIndex)

    setOrderedProjects(reordered)
    setSavingOrder(true)
    setOrderError(null)

    try {
      await Promise.all(
        reordered.map((project, index) =>
          adminFilmsService.updateFilm(project.id, { order: index + 1 }),
        ),
      )
      refresh()
    } catch (err) {
      setOrderError(formatUserFacingError(err, 'Failed to save order'))
    } finally {
      setSavingOrder(false)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeletingId(deleteTarget.id)
    try {
      await deleteProject(deleteTarget.id)
    } finally {
      setDeletingId(null)
      setDeleteTarget(null)
      setDialogOpen(false)
    }
  }

  function openDeleteDialog(project: Project) {
    setDeleteTarget(project)
    setDialogOpen(true)
  }

  return (
    <AdminListPage
      title="Projects"
      action={
        <Button asChild>
          <Link to="/admin/films/new?type=project">Add Project</Link>
        </Button>
      }
      error={error ?? orderError}
      loading={loading}
      dialog={
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger className="hidden" />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Project</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-foreground">{deleteTarget?.title}</span>? This
              action cannot be undone.
            </p>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDialogOpen(false)
                  setDeleteTarget(null)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleDelete()}
                disabled={deletingId !== null}
              >
                {deletingId !== null ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {savingOrder && (
        <p className="text-xs text-muted-foreground -mt-2 mb-2">Saving order…</p>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(e) => void handleDragEnd(e)}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Title</TableHead>
              <TableHead>Published</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <MultiButtonGroup>
            <SortableContext
              items={orderedProjects.map((p) => p.id)}
              strategy={verticalListSortingStrategy}
            >
              <TableBody>
                {orderedProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No projects found. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  orderedProjects.map((project) => (
                    <SortableProjectRow
                      key={project.id}
                      project={project}
                      deletingId={deletingId}
                      onEdit={(id) => navigate(`/admin/films/${id}/edit`)}
                      onDelete={openDeleteDialog}
                    />
                  ))
                )}
              </TableBody>
            </SortableContext>
          </MultiButtonGroup>
        </Table>
      </DndContext>
    </AdminListPage>
  )
}
